import axios from 'axios';
import to from 'await-to-js';
import _ from 'lodash';
import { useZones, useStoreGroupChains } from '@enums/feature-flags';
import sortStoreGroups from '../../utils/sort-store-groups-util';

const getInitialState = () => {
  return {
    storeGroups: [],
    storeGroupRelationships: [],
    storeGroupChains: [],
    loadingStoreGroupRelationships: false,
    mainTsgKey: null,
    storegroupKeyNameMap: null,
    workpackageStoreGroups: [],
  };
};

const store = {
  namespaced: true,

  state: getInitialState(),

  mutations: {
    resetState(state) {
      Object.assign(state, getInitialState());
    },

    setStoreGroups(state, storeGroups) {
      state.storeGroups = storeGroups;
    },

    setStoreGroupRelationships(state, relationships) {
      state.storeGroupRelationships = relationships;
    },

    setStoreGroupChains(state, chains) {
      state.storeGroupChains = chains;
    },

    setLoadingStoreGroupRelationships(state, loading) {
      state.loadingStoreGroupRelationships = loading;
    },

    setStoregroupKeyNameMap(state, value) {
      state.storegroupKeyNameMap = value;
    },

    setWorkpackageStoreGroups(state, value) {
      state.workpackageStoreGroups = value;
    },
  },

  getters: {
    // todo: swap to workpackage? data already exists there
    mainToolStoreGroupKeys: (state, getters, rootState) => {
      // Get the main store group from config if available
      const mainTsg = _.get(rootState, 'clientConfig.chainsConfig.mainTsg', '');
      const useStoreGroupChainsFlag = _.get(
        rootState,
        ['clientConfig', 'toggleLogic', useStoreGroupChains],
        false
      );

      // Use mainTsg only when useStoreGroupChainsFlag feature flag is enabled and mainTsg exists
      if (useStoreGroupChainsFlag && !_.isEmpty(mainTsg)) {
        const mainStoreGroup = _.find(
          state.workpackageStoreGroups,
          sg => sg.toolStoreGroupDescription === mainTsg
        );
        if (mainStoreGroup) {
          return [mainStoreGroup.toolStoreGroupKey];
        }
      }

      return state.workpackageStoreGroups
        .filter(sg => sg.parentPricingStoreGroupKey === null)
        .map(sg => sg.toolStoreGroupKey);
    },

    // PRICE-2315 tmp hack for dzh.
    // TODO: remove these and revert to original values provided by vuex
    hardcodedStoreGroups: (state, getters, rootState) => {
      const workpackageDescription = _.get(
        rootState.workpackages,
        'selectedWorkpackage.description',
        ''
      );
      return _.get(
        rootState.clientConfig,
        `wholesaleConfig.hardcodedStoreGroups.${workpackageDescription}`,
        []
      );
    },
    hackedSelectedWorkpackageToolStoreGroups: (state, getters) => {
      return getters.hardcodedStoreGroups.map(v => ({
        ...v,
        description: v.toolStoreGroupDescription,
        key: v.toolStoreGroupKey,
      }));
    },
    allHardcodedStoreGroups: (state, getters, rootState) => {
      return _.get(rootState.clientConfig, 'wholesaleConfig.hardcodedStoreGroups', {});
    },

    /**
     * Returns store groups as a sorted array of { toolStoreGroupKey, toolStoreGroupDescription }
     * objects. Order is determined by storeGroupOrderConfig, applied when storegroupKeyNameMap is
     * fetched via fetchStoregroupKeyNameMap.
     */
    sortedStoreGroups: state => {
      if (!state.storegroupKeyNameMap) return [];
      return Object.entries(state.storegroupKeyNameMap).map(
        ([toolStoreGroupKey, toolStoreGroupDescription]) => ({
          toolStoreGroupKey,
          toolStoreGroupDescription,
        })
      );
    },

    /**
     * Maps each toolStoreGroupKey to the chain TSG key for its chain.
     * The chain TSG is identified as the TSG whose description matches the chain's description.
     */
    chainTsgKeyByTsgKey: (state, getters, rootState) => {
      const useStoreGroupChainsFlag = rootState.clientConfig.toggleLogic[useStoreGroupChains];
      const useZonesFlag = rootState.clientConfig.toggleLogic[useZones];

      if (!useZonesFlag || !useStoreGroupChainsFlag || !state.storegroupKeyNameMap) {
        return {};
      }

      return state.storeGroupChains.reduce((acc, chain) => {
        const chainTsgKey = chain.toolStoreGroupKeys.find(
          tsgKey => state.storegroupKeyNameMap[tsgKey] === chain.description
        );
        if (chainTsgKey) {
          chain.toolStoreGroupKeys.forEach(tsgKey => {
            acc[tsgKey] = chainTsgKey;
          });
        }
        return acc;
      }, {});
    },

    toolStoreGroupsDescriptionMap: (state, getters) => {
      return _.reduce(
        _.isEmpty(getters.hardcodedStoreGroups)
          ? getters.storeGroupRelationships
          : getters.hardcodedStoreGroups,
        (acc, item) => {
          acc[item.toolStoreGroupKey] = item.toolStoreGroupDescription;
          return acc;
        },
        {}
      );
    },
  },

  actions: {
    resetState({ commit }) {
      commit('resetState');
    },

    async fetchStoregroupKeyNameMap({ commit, rootState }, workpackageId = null) {
      if (!workpackageId) {
        workpackageId = rootState.workpackages.selectedWorkpackage._id;
      }

      const [err, data] = await to(
        axios.get(
          `/api/store-group-relationships/workpackage/${workpackageId}/workpackage-description-map`
        )
      );
      if (err) console.error(err);

      const fetchedMap = _.get(data, 'data', null);

      const storeGroupOrderConfig = _.get(rootState, 'clientConfig.storeGroupOrderConfig', {});

      // if no specific order config, commit the fetched map as is
      if (_.isEmpty(storeGroupOrderConfig)) {
        commit('setStoregroupKeyNameMap', fetchedMap);
        return;
      }

      // convert the fetched map to an array of objects for sorting
      const storeGroupsArray = _.map(fetchedMap, (description, key) => ({
        toolStoreGroupKey: key,
        toolStoreGroupDescription: description,
      }));

      const orderedGroupsArray = sortStoreGroups(
        storeGroupsArray,
        storeGroupOrderConfig,
        'toolStoreGroupDescription'
      );

      // Convert the sorted array of objects back into a map
      const orderedMap = _.reduce(
        orderedGroupsArray,
        (result, item) => {
          result[item.toolStoreGroupKey] = item.toolStoreGroupDescription;
          return result;
        },
        {}
      );

      commit('setStoregroupKeyNameMap', orderedMap);
    },

    async fetchStoreGroupRelationships({ commit, rootState }) {
      const useZonesFlag = rootState.clientConfig.toggleLogic[useZones];
      if (!useZonesFlag) {
        commit('setStoreGroupRelationships', []);
        return;
      }
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      if (!workpackageId) return;
      commit('setLoadingStoreGroupRelationships', true);
      const [err, res] = await to(
        axios.get(`/api/store-group-relationships/workpackage/${workpackageId}`)
      );

      if (err) {
        commit('setStoreGroupRelationships', []);
        commit('setLoadingStoreGroupRelationships', false);
        throw new Error(err.message);
      }
      commit('setStoreGroupRelationships', res.data);
      commit('setLoadingStoreGroupRelationships', false);

      return res.data;
    },

    async fetchStoreGroupChains({ commit, rootState }) {
      const useZonesFlag = rootState.clientConfig.toggleLogic[useZones];
      const useStoreGroupChainsFlag = rootState.clientConfig.toggleLogic[useStoreGroupChains];
      if (!useZonesFlag || !useStoreGroupChainsFlag) {
        commit('setStoreGroupChains', []);
        return;
      }
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      if (!workpackageId) return;
      const [err, res] = await to(
        axios.get(
          `/api/store-group-relationships/workpackage/${workpackageId}/workpackage-store-group-chains`
        )
      );

      if (err) {
        commit('setStoreGroupChains', []);
        console.error(err);
      }
      commit('setStoreGroupChains', res.data);
    },

    async fetchWorkpackageStoreGroups({ commit, rootState }) {
      const useZonesFlag = rootState.clientConfig.toggleLogic[useZones];
      if (!useZonesFlag) {
        commit('setWorkpackageStoreGroups', []);
        return;
      }
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      if (!workpackageId) return;

      const [err, res] = await to(
        axios.get(
          `/api/store-group-relationships/workpackage/${workpackageId}/workpackage-with-parent`
        )
      );

      if (err) throw new Error(err.message);

      commit('setWorkpackageStoreGroups', res.data);
      return res.data;
    },

    // temporary fix for PRICE-2755
    // store-group-relationships collection is populated by a script and can get out of sync with the datafeed.
    // created new api endpoint to read from workpacakge-store-group-relationships only and store here
    // TODO: remove this after PRICE-2796 and rely on storeGroupRelationships
    async fetchStoreGroups({ commit, rootState }) {
      const useZonesFlag = rootState.clientConfig.toggleLogic[useZones];
      if (!useZonesFlag) {
        commit('setStoreGroups', []);
        return;
      }
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      if (!workpackageId) return;
      const [err, res] = await to(
        axios.get(
          `/api/store-group-relationships/workpackage/${workpackageId}/workpackage-store-groups`
        )
      );

      if (err) {
        commit('setStoreGroups', []);
        throw new Error(err.message);
      }
      commit('setStoreGroups', res.data);

      return res.data;
    },
  },
};

export default store;
